/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../../logging/Log";
import type { IPlayer } from "../../interfaces";
import { Direction, SpeedLevel, TrickPlayMode } from "../../enums";
import { SPEED_VALUE } from "../../types";
import type { ITrickPlayControl, ITrickPlayConfig } from "../../interfaces";

/**
 * I-Frame trick-play implementation.
 *
 * Used when the loaded manifest contains a dedicated trick-play track
 * (e.g. Vecima / Velocix VTP DASH `trickModeVideo` or HLS
 * `EXT-X-I-FRAME-STREAM-INF`). Delegates to IPlayer.setTrickPlayRate()
 * which renders full-screen i-frames at the requested rate.
 *
 * Unlike Thumbnail/Emulated modes, the player IS positioned at the
 * seek target via the i-frame stream during the session (TP-SP-8).
 * On confirm, no additional seek is needed — playback just continues.
 *
 * During scrub, computes a proportional rate from the drag delta
 * and feeds it to the player's trick-play track. On session end,
 * calls cancelTrickPlay() and commits the final position.
 */
export class IFrameTrickplayControlImpl implements ITrickPlayControl {
  private static readonly TAG: string = "IFrameTrickplayControlImpl";

  public readonly mode: TrickPlayMode = TrickPlayMode.IFrame;

  /** Reference to the active player instance. */
  private mPlayer: IPlayer;

  /** Trick-play configuration values. */
  private mConfig: ITrickPlayConfig;

  /** Last scrub position for computing proportional rate deltas. */
  private mLastScrubPosition: number = 0;

  constructor(player: IPlayer, config: ITrickPlayConfig) {
    this.mPlayer = player;
    this.mConfig = config;
  }

  // ── Private Methods ──────────────────────────────────────────────────────

  /**
   * Compute a trick-play rate proportional to the scrub delta.
   * Positive delta -> forward rate; negative delta -> backward rate.
   * Clamped to +/-SPEED_VALUE[L3] to avoid excessive rates.
   */
  private computeRateFromDelta(targetPos: number): number {
    const delta = targetPos - this.mLastScrubPosition;
    const maxRate = SPEED_VALUE[SpeedLevel.L3];

    if (delta === 0) {
      return SPEED_VALUE[SpeedLevel.L1];
    }

    // Scale: small drag = L1, medium = L2, large = L3
    const absDelta = Math.abs(delta);
    let rate: number;
    if (absDelta <= 5) {
      rate = SPEED_VALUE[SpeedLevel.L1];
    } else if (absDelta <= 30) {
      rate = SPEED_VALUE[SpeedLevel.L2];
    } else {
      rate = SPEED_VALUE[SpeedLevel.L3];
    }

    const sign = delta > 0 ? 1 : -1;
    return Math.max(-maxRate, Math.min(maxRate, rate * sign));
  }

  // ── ITrickPlayControl ────────────────────────────────────────────────────

  public skip(direction: Direction): void {
    const step =
      direction === Direction.Forward
        ? this.mConfig.forwardStep
        : this.mConfig.backwardStep;
    const target = this.mPlayer.getCurrentTime() + direction * step;
    this.mPlayer.pause();
    this.mPlayer.seek(target);
    Log.d(
      IFrameTrickplayControlImpl.TAG,
      "skip() direction=" + direction + " target=" + target
    );
  }

  public fastForward(direction: Direction, level: SpeedLevel): void {
    const rate = SPEED_VALUE[level] * direction;
    Log.d(
      IFrameTrickplayControlImpl.TAG,
      "fastForward() rate=" + rate + " level=" + level
    );
    this.mPlayer.setTrickPlayRate(rate, true);
  }

  public onScrubBegin(positionSec: number): void {
    this.mLastScrubPosition = positionSec;
    Log.d(IFrameTrickplayControlImpl.TAG, "onScrubBegin() pos=" + positionSec);
  }

  public onScrubMove(positionSec: number): void {
    const rate = this.computeRateFromDelta(positionSec);
    this.mPlayer.setTrickPlayRate(rate, true);
    this.mLastScrubPosition = positionSec;
  }

  public onScrubEnd(positionSec: number): void {
    this.mPlayer.cancelTrickPlay();
    this.mPlayer.seek(positionSec);
    Log.d(IFrameTrickplayControlImpl.TAG, "onScrubEnd() pos=" + positionSec);
  }

  public getTargetPosition(): number {
    // IFrame mode: player is already at target via the i-frame stream.
    return this.mPlayer.getCurrentTime();
  }

  public pauseScrubbing(): void {
    // Cancel the i-frame rate but keep the player at the current position.
    this.mPlayer.cancelTrickPlay();
    Log.d(IFrameTrickplayControlImpl.TAG, "pauseScrubbing()");
  }

  public stopScrubbing(): void {
    this.mPlayer.cancelTrickPlay();
    Log.d(IFrameTrickplayControlImpl.TAG, "stopScrubbing()");
  }
}
